import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { Brand } from '../utils/api';
import { LineChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { PALETTE, fmtMonth } from '../utils/theme';
import { isRideHailing } from '../utils/oneHr';

function buildAppSeries(rows, months6, valueKey) {
  const apps = [...new Set(rows.map(r => r.application))];
  return apps.map((app, i) => {
    const byMonth = Object.fromEntries(rows.filter(r => r.application === app).map(r => [r.month_id, r[valueKey]]));
    return { name: app, data: months6.map(m => byMonth[m] ?? null), color: PALETTE[i % PALETTE.length] };
  });
}

// Returns last N months ending at selectedMonth
function last6(allMonths, selectedMonth) {
  const idx = allMonths.indexOf(selectedMonth);
  if (idx < 0) return allMonths.slice(-6);
  return allMonths.slice(Math.max(0, idx - 5), idx + 1);
}

export default function TrendsNationView() {
  const { category }  = useCategory();
  const { months, sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const { month }                     = useMonth();
  const [sessionType, setSessionType] = useState('open_app');

  const effectiveCat    = category || 'ALL';
  const selectedMonth   = month || latestMonth;

  // Ride Hailing & Taxi always uses 1-hour sessions (legacy table, through Apr 2026).
  const is1hr = isRideHailing(category);

  const { data, loading, error } = useApi(
    () => Brand.appTrend(effectiveCat, sessionType, is1hr ? '1hr' : undefined),
    { effectiveCat, sessionType, is1hr },
    !!months.length,
  );

  // 1hr legacy data ends at Apr 2026, so derive the 6-month window from the data
  // itself; otherwise use the last 6 months up to the selected month.
  const months6 = useMemo(() => {
    if (is1hr) return [...new Set((data || []).map(r => r.month_id))].sort().slice(-6);
    return last6(months, selectedMonth);
  }, [is1hr, data, months, selectedMonth]);

  const userSeries    = useMemo(() => data ? buildAppSeries(data, months6, 'users')        : [], [data, months6]);
  const sessionSeries = useMemo(() => data ? buildAppSeries(data, months6, 'visits_15min')   : [], [data, months6]);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {is1hr && (
        <div style={{
          background: 'rgba(29,184,154,0.08)', border: '1px solid rgba(29,184,154,0.25)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)',
        }}>
          Showing <strong>1-hour continuous sessions</strong> from the legacy dataset —
          always used for <strong>Ride Hailing &amp; Taxi</strong> (through Apr 2026).
        </div>
      )}

      <div className="trends-section-label">All Apps — 6-Month Trend</div>

      <ChartCard title="Monthly Active Users by App" sub={`${effectiveCat} · ${sessionType}`} style={{ marginBottom: 16 }}>
        {loading  && <ChartLoader height={300} />}
        {error    && <ChartError message={error} height={300} />}
        {!loading && userSeries.length > 0 && <LineChart categories={months6.map(fmtMonth)} series={userSeries} height={300} />}
      </ChartCard>

      <ChartCard title={`Monthly Sessions by App${is1hr ? ' (1-hour)' : ''}`} sub={`${effectiveCat} · ${sessionType}`}>
        {loading  && <ChartLoader height={300} />}
        {error    && <ChartError message={error} height={300} />}
        {!loading && sessionSeries.length > 0 && <LineChart categories={months6.map(fmtMonth)} series={sessionSeries} height={300} />}
      </ChartCard>
    </div>
  );
}
